import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorDetectiveItem } from '../../../types/lesson';
import { useLessonPersistedState } from '../useLessonPersistedState';
import TranslateToggle from './TranslateToggle';

interface Props {
    lessonId?: number;
    items: ErrorDetectiveItem[];
    teacherMode: boolean;
    onWordAnswered?: (word: string, correct: boolean, hintUsed?: boolean) => void;
    sourceLanguage?: string;
    targetLanguage?: string;
}

// Apostrophes are kept (curly ones folded to straight): they're often the very thing an item
// tests ("it's" → "its", "your" → "you're"), so stripping them made the flawed and corrected
// sentences normalize identically.
function normalize(s: string): string {
    return s.trim().toLowerCase().replace(/[’‘]/g, "'").replace(/[.,!?]/g, '').replace(/\s+/g, ' ');
}

export function isCorrect(answer: string, item: ErrorDetectiveItem): boolean {
    const norm = normalize(answer);
    // The input starts prefilled with the flawed sentence — submitting it unedited is never a fix,
    // even if some normalization quirk made it compare equal to a correction.
    if (norm === normalize(item.flawed_sentence)) return false;
    return item.expected_corrections.some((c) => normalize(c) === norm);
}

// Best-effort extraction of "the word this item is testing" from a flawed/corrected sentence
// pair. Since the generation prompt now requires expected_corrections[0] to be flawed_sentence
// with exactly one word/phrase fixed (see the CRITICAL rule added for this), a same-length,
// single-differing-token diff is usually reliable — but never guess when that assumption doesn't
// hold (different word counts, or more than one token differs): return null and skip the item
// rather than reporting a wrong word.
export function diffTestedWord(flawed: string, corrected: string): string | null {
    // Apostrophes kept for the same reason as normalize(): "it's" vs "its" IS the tested word.
    const tokenize = (s: string) => s.replace(/[’‘]/g, "'").replace(/[.,!?;:"]/g, '').trim().split(/\s+/);
    const a = tokenize(flawed);
    const b = tokenize(corrected);
    if (a.length !== b.length) return null;
    const diffIndexes = a.reduce<number[]>((acc, word, i) => (word.toLowerCase() === b[i].toLowerCase() ? acc : [...acc, i]), []);
    return diffIndexes.length === 1 ? b[diffIndexes[0]] : null;
}

const ErrorDetectiveSection: React.FC<Props> = ({ lessonId, items, teacherMode, onWordAnswered, sourceLanguage, targetLanguage }) => {
    const { t } = useTranslation('lesson');
    const [answers, setAnswers] = useLessonPersistedState<Record<number, string>>(lessonId, 'errordetective.answers', {});
    const [checked, setChecked] = useLessonPersistedState(lessonId, 'errordetective.checked', false);
    const [score, setScore] = useLessonPersistedState<number | null>(lessonId, 'errordetective.score', null);
    const [showTranslation, setShowTranslation] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'errordetective.showTranslation', {});
    const [translationFallback, setTranslationFallback] = useLessonPersistedState<Record<number, string>>(lessonId, 'errordetective.translationFallback', {});
    const [showCorrectionTranslation, setShowCorrectionTranslation] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'errordetective.showCorrectionTranslation', {});
    const [correctionFallback, setCorrectionFallback] = useLessonPersistedState<Record<number, string>>(lessonId, 'errordetective.correctionFallback', {});
    // Pre-check icon (flawed_sentence) can hint at the fix — opening it before "Check Answers" is
    // clicked flags that item for a reduced score at Finish.
    const [showFlawedTranslation, setShowFlawedTranslation] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'errordetective.showFlawedTranslation', {});
    const [flawedFallback, setFlawedFallback] = useLessonPersistedState<Record<number, string>>(lessonId, 'errordetective.flawedFallback', {});
    const [hintUsed, setHintUsed] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'errordetective.hintUsed', {});

    const markHint = (i: number) => {
        if (checked) return;
        setHintUsed((prev) => (prev[i] ? prev : { ...prev, [i]: true }));
    };

    // Prepopulated with the flawed sentence itself (see input below) so the student edits it in
    // place to fix the mistake, instead of retyping the whole sentence from scratch — left
    // unedited, it normalizes differently from every expected_corrections entry (the flaw is a
    // wording/grammar difference, not just punctuation), so it's never accidentally graded correct.
    const answerFor = (i: number, item: ErrorDetectiveItem) => answers[i] ?? item.flawed_sentence;

    const checkAnswers = () => {
        let s = 0;
        items.forEach((item, i) => {
            const correct = isCorrect(answerFor(i, item), item);
            if (correct) s++;
            const word = diffTestedWord(item.flawed_sentence, item.expected_corrections[0]);
            if (word) onWordAnswered?.(word, correct, hintUsed[i]);
        });
        setScore(s);
        setChecked(true);
    };

    return (
        <div className="lesson-panel">
            <div>
                <div className="lesson-panel-kicker">{t('sections.errors.kicker', 'Practice')}</div>
                <h2>{t('sections.errors.title', 'Error Detective')}</h2>
                <p className="lesson-panel-desc">{t('sections.errors.desc', 'Each sentence has one mistake. Type the corrected sentence.')}</p>
            </div>
            <div className="lesson-qgrid">
                {items.map((item, i) => {
                    const correct = checked && isCorrect(answerFor(i, item), item);
                    return (
                        <div className="lesson-qcard" key={i}>
                            <div className="lesson-qnum">{t('sections.sentence_n', 'Sentence {{n}}', { n: i + 1 })}</div>
                            <div className="lesson-word-target">
                                ❌ {item.flawed_sentence}
                                <TranslateToggle
                                    text={item.flawed_sentence}
                                    fallback={flawedFallback[i]}
                                    onFallbackFetched={(text) => setFlawedFallback((prev) => ({ ...prev, [i]: text }))}
                                    sourceLanguage={sourceLanguage}
                                    targetLanguage={targetLanguage}
                                    shown={!!showFlawedTranslation[i]}
                                    onToggle={() => { markHint(i); setShowFlawedTranslation((prev) => ({ ...prev, [i]: !prev[i] })); }}
                                />
                            </div>
                            <input
                                className={`lesson-input-line${checked ? (correct ? ' correct' : ' incorrect') : ''}`}
                                disabled={checked}
                                value={answerFor(i, item)}
                                onChange={(e) => setAnswers((prev) => ({ ...prev, [i]: e.target.value }))}
                            />
                            {(checked || teacherMode) && (
                                <div className="lesson-reveal lesson-word-target">
                                    <div className="lesson-reveal-explanation">
                                        <strong>✅ {item.expected_corrections[0]}</strong>
                                        <TranslateToggle
                                            text={item.expected_corrections[0]}
                                            native={item.expected_corrections_native}
                                            fallback={correctionFallback[i]}
                                            onFallbackFetched={(text) => setCorrectionFallback((prev) => ({ ...prev, [i]: text }))}
                                            sourceLanguage={sourceLanguage}
                                            targetLanguage={targetLanguage}
                                            shown={!!showCorrectionTranslation[i]}
                                            onToggle={() => setShowCorrectionTranslation((prev) => ({ ...prev, [i]: !prev[i] }))}
                                        />
                                    </div>
                                    <div className="lesson-reveal-explanation">
                                        {item.error_explanation}
                                        <TranslateToggle
                                            text={item.error_explanation}
                                            native={item.error_explanation_native}
                                            fallback={translationFallback[i]}
                                            onFallbackFetched={(text) => setTranslationFallback((prev) => ({ ...prev, [i]: text }))}
                                            sourceLanguage={sourceLanguage}
                                            targetLanguage={targetLanguage}
                                            shown={!!showTranslation[i]}
                                            onToggle={() => setShowTranslation((prev) => ({ ...prev, [i]: !prev[i] }))}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="lesson-actions-row">
                <button className="lesson-btn" onClick={checkAnswers} disabled={checked}>
                    {t('sections.check_answers', 'Check Answers')}
                </button>
                {score !== null && (
                    <div className="lesson-score-pill">{t('sections.score', '{{score}} / {{total}} correct', { score, total: items.length })}</div>
                )}
            </div>
        </div>
    );
};

export default ErrorDetectiveSection;
